// src/components/Cockpit/WorkflowLiveDag.tsx
// ============================================
// WorkflowLiveDag - 实时动画 DAG（Cockpit 2.0 / Sprint 3 Task 2）
//
// 设计理念：从静态 mini DAG 升级为 Airflow 式实时动画 DAG
//
// 展示 Workflow 执行的完整流程：
//   Knowledge → Writer → Reviewer → Publisher → Memory
//
// 视觉：
//   - SVG 绘制节点 + 连线
//   - 节点状态色 + 脉冲动画
//   - 连线上的数据流动画（小光点沿边移动）
//   - 顶部：当前执行的工作流名称 + 进度
//   - 节点悬浮显示详情
// ============================================

import { motion } from "framer-motion";
import {
  BookOpen,
  PenLine,
  CheckSquare,
  Send,
  Brain,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ============================================
// 类型定义
// ============================================

export type DagNodeStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "skipped";

export interface DagNode {
  id: string;
  label: string;
  icon: LucideIcon;
  status: DagNodeStatus;
  /** 节点位置（百分比） */
  x: number;
  y: number;
  /** 耗时（毫秒） */
  duration?: number;
  color: string;
}

export interface DagEdge {
  from: string;
  to: string;
  /** 数据流标签 */
  label?: string;
}

export interface DagExecution {
  /** 工作流名称 */
  workflowName: string;
  /** 执行 ID */
  executionId: string;
  /** 已完成节点数 */
  completedNodes: number;
  /** 总节点数 */
  totalNodes: number;
  nodes: DagNode[];
  edges: DagEdge[];
}

// ============================================
// 默认数据
// ============================================

// DAG 画布坐标系：viewBox = "0 0 1000 300"
// 节点按横向均匀分布，y=150（垂直居中）
// 5 个节点时 x: 80, 290, 500, 710, 920
// 节点半径 r=22，foreignObject 用像素级尺寸避免标签堆叠

export const DAG_VIEWBOX = { width: 1000, height: 300, nodeY: 150, nodeR: 22 };

// 根据节点数量计算均匀分布的 x 坐标
export function computeNodeXs(count: number): number[] {
  if (count <= 1) return [DAG_VIEWBOX.width / 2];
  const margin = 80;
  const span = DAG_VIEWBOX.width - margin * 2;
  return Array.from({ length: count }, (_, i) =>
    Math.round(margin + (span * i) / (count - 1))
  );
}

export const DEFAULT_DAG_EXECUTION: DagExecution = {
  workflowName: "内容生产管线",
  executionId: "wf_8a2f3c",
  completedNodes: 3,
  totalNodes: 5,
  nodes: [
    {
      id: "knowledge",
      label: "Knowledge",
      icon: BookOpen,
      status: "completed",
      x: 80,
      y: 150,
      duration: 380,
      color: "#38BDF8",
    },
    {
      id: "writer",
      label: "Writer",
      icon: PenLine,
      status: "completed",
      x: 290,
      y: 150,
      duration: 1280,
      color: "#EC4899",
    },
    {
      id: "reviewer",
      label: "Reviewer",
      icon: CheckSquare,
      status: "completed",
      x: 500,
      y: 150,
      duration: 420,
      color: "#34D399",
    },
    {
      id: "publisher",
      label: "Publisher",
      icon: Send,
      status: "running",
      x: 710,
      y: 150,
      color: "#A78BFA",
    },
    {
      id: "memory",
      label: "Memory",
      icon: Brain,
      status: "pending",
      x: 920,
      y: 150,
      color: "#6B7280",
    },
  ],
  edges: [
    { from: "knowledge", to: "writer", label: "context" },
    { from: "writer", to: "reviewer", label: "draft" },
    { from: "reviewer", to: "publisher", label: "approved" },
    { from: "publisher", to: "memory", label: "result" },
  ],
};

// ============================================
// 状态 → 图标
// ============================================

function NodeStatusIcon({
  status,
  color,
}: {
  status: DagNodeStatus;
  color: string;
}) {
  switch (status) {
    case "completed":
      return <CheckCircle2 size={10} style={{ color }} />;
    case "running":
      return (
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 size={10} style={{ color }} />
        </motion.span>
      );
    case "failed":
      return <AlertCircle size={10} style={{ color: "#F87171" }} />;
    case "skipped":
      return <span className="text-[8px] text-gray-600">—</span>;
    default:
      return <Clock size={10} style={{ color: "#6B7280" }} />;
  }
}

// ============================================
// 数据流动画（沿边移动的光点）
// ============================================

function DataFlowDot({
  from,
  to,
  color,
  delay,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  color: string;
  delay: number;
}) {
  return (
    <motion.circle
      r={2}
      fill={color}
      style={{ filter: `drop-shadow(0 0 4px ${color})` }}
      initial={{ cx: from.x, cy: from.y, opacity: 0 }}
      animate={{
        cx: [from.x, to.x],
        cy: [from.y, to.y],
        opacity: [0, 1, 1, 0],
      }}
      transition={{
        duration: 1.8,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}

// ============================================
// DAG 节点
// ============================================

function DagNodeView({
  node,
  index,
}: {
  node: DagNode;
  index: number;
}) {
  const Icon = node.icon;
  const isRunning = node.status === "running";
  const isCompleted = node.status === "completed";
  const r = DAG_VIEWBOX.nodeR;

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: 0.1 + index * 0.1 }}
    >
      {/* 运行中的呼吸光晕 */}
      {isRunning && (
        <motion.circle
          cx={node.x}
          cy={node.y}
          r={r + 8}
          fill={node.color}
          opacity={0.15}
          animate={{ r: [r + 4, r + 12, r + 4], opacity: [0.1, 0.25, 0.1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        />
      )}

      {/* 节点主体 */}
      <circle
        cx={node.x}
        cy={node.y}
        r={r}
        fill={`${node.color}${isCompleted ? "30" : isRunning ? "25" : "10"}`}
        stroke={node.color}
        strokeWidth={isRunning ? 2 : 1}
        strokeOpacity={isCompleted || isRunning ? 0.8 : 0.4}
        style={{
          filter: isRunning
            ? `drop-shadow(0 0 8px ${node.color})`
            : isCompleted
            ? `drop-shadow(0 0 4px ${node.color}80)`
            : "none",
        }}
      />

      {/* 节点图标（foreignObject 嵌入 SVG 图标） */}
      <foreignObject
        x={node.x - 12}
        y={node.y - 12}
        width={24}
        height={24}
      >
        <div className="w-full h-full flex items-center justify-center">
          <Icon size={16} style={{ color: node.color }} />
        </div>
      </foreignObject>

      {/* 状态指示器（右上角） */}
      <foreignObject
        x={node.x + r - 6}
        y={node.y - r - 6}
        width={20}
        height={20}
      >
        <div className="w-full h-full flex items-center justify-center bg-[#0B1220] rounded-full border border-white/10">
          <NodeStatusIcon status={node.status} color={node.color} />
        </div>
      </foreignObject>

      {/* 节点标签（下方）—— 宽度 120 在 viewBox 1000 下占 12%，标签不会堆叠 */}
      <foreignObject
        x={node.x - 60}
        y={node.y + r + 8}
        width={120}
        height={36}
      >
        <div className="w-full text-center">
          <div
            className="text-[11px] font-mono font-semibold leading-tight"
            style={{ color: isCompleted || isRunning ? node.color : "#9CA3AF" }}
          >
            {node.label}
          </div>
          {node.duration !== undefined && (
            <div className="text-[9px] font-mono text-gray-500 leading-tight">
              {node.duration}ms
            </div>
          )}
        </div>
      </foreignObject>
    </motion.g>
  );
}

// ============================================
// DAG 边
// ============================================

function DagEdgeView({
  edge,
  nodes,
  index,
}: {
  edge: DagEdge;
  nodes: DagNode[];
  index: number;
}) {
  const fromNode = nodes.find((n) => n.id === edge.from);
  const toNode = nodes.find((n) => n.id === edge.to);
  if (!fromNode || !toNode) return null;

  // 边的颜色：从源节点色渐变到目标节点色
  const fromCompleted = fromNode.status === "completed";
  const toRunning = toNode.status === "running";
  const toCompleted = toNode.status === "completed";
  const active = fromCompleted && (toRunning || toCompleted);

  const edgeColor = active ? toNode.color : "#374151";
  const edgeOpacity = active ? 0.8 : 0.3;

  // 计算边的中点（用于数据流光点）
  const midX = (fromNode.x + toNode.x) / 2;
  const midY = (fromNode.y + toNode.y) / 2;

  return (
    <motion.g
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: 0.05 + index * 0.1 }}
    >
      {/* 边线 */}
      <line
        x1={fromNode.x + DAG_VIEWBOX.nodeR}
        y1={fromNode.y}
        x2={toNode.x - DAG_VIEWBOX.nodeR}
        y2={toNode.y}
        stroke={edgeColor}
        strokeWidth={active ? 1.5 : 1}
        strokeOpacity={edgeOpacity}
        strokeDasharray={active ? "0" : "3,3"}
      />

      {/* 数据流光点（仅在边激活时显示） */}
      {active && (
        <>
          <DataFlowDot
            from={{ x: fromNode.x + DAG_VIEWBOX.nodeR, y: fromNode.y }}
            to={{ x: toNode.x - DAG_VIEWBOX.nodeR, y: toNode.y }}
            color={toNode.color}
            delay={index * 0.3}
          />
          <DataFlowDot
            from={{ x: fromNode.x + DAG_VIEWBOX.nodeR, y: fromNode.y }}
            to={{ x: toNode.x - DAG_VIEWBOX.nodeR, y: toNode.y }}
            color={toNode.color}
            delay={index * 0.3 + 0.9}
          />
        </>
      )}

      {/* 边标签 */}
      {edge.label && (
        <foreignObject
          x={midX - 40}
          y={midY - 10}
          width={80}
          height={14}
        >
          <div className="w-full text-center">
            <span className="text-[9px] font-mono text-gray-500 bg-[#0B1220]/80 px-1 rounded">
              {edge.label}
            </span>
          </div>
        </foreignObject>
      )}
    </motion.g>
  );
}

// ============================================
// WorkflowLiveDag 容器
// ============================================

interface WorkflowLiveDagProps {
  execution?: DagExecution;
}

export default function WorkflowLiveDag({
  execution = DEFAULT_DAG_EXECUTION,
}: WorkflowLiveDagProps) {
  const progressPct =
    execution.totalNodes === 0
      ? 0
      : (execution.completedNodes / execution.totalNodes) * 100;

  return (
    <div className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-4 h-full flex flex-col overflow-hidden">
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

      {/* 头部 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-cyan-400 to-cyan-600 rounded-full" />
          <h3 className="text-sm font-medium text-white">Workflow DAG</h3>
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            {execution.executionId}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono">
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-cyan-400"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <span className="text-cyan-400 font-bold">{execution.workflowName}</span>
        </div>
      </div>

      {/* 进度条 */}
      <div className="mb-2 flex items-center gap-2">
        <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{
              background: "linear-gradient(90deg, #34D399, #38BDF8, #A78BFA)",
              boxShadow: "0 0 6px rgba(56,189,248,0.6)",
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <span className="text-[10px] font-mono text-gray-400">
          <span className="text-emerald-400 font-bold">
            {execution.completedNodes}
          </span>
          <span className="text-gray-600"> / </span>
          <span>{execution.totalNodes}</span>
        </span>
      </div>

      {/* DAG 画布（SVG） */}
      <div className="flex-1 relative min-h-0">
        <svg
          viewBox={`0 0 ${DAG_VIEWBOX.width} ${DAG_VIEWBOX.height}`}
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-full"
        >
          {/* 先画边 */}
          {execution.edges.map((edge, idx) => (
            <DagEdgeView
              key={`${edge.from}-${edge.to}`}
              edge={edge}
              nodes={execution.nodes}
              index={idx}
            />
          ))}
          {/* 再画节点（在边之上） */}
          {execution.nodes.map((node, idx) => (
            <DagNodeView key={node.id} node={node} index={idx} />
          ))}
        </svg>
      </div>
    </div>
  );
}
